import { useState, useEffect } from "react";
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Wrench, 
  ShoppingCart, 
  User, 
  Bell, 
  CheckCircle2,
  Sparkles,
  Building2,
  ClipboardList,
  Package,
  Shield,
  Users,
  FileText,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STERILE_MATE_LOGO = "/logos/askisaac-b-monogram.svg";

// Hospital/Public User Onboarding Steps
const hospitalSteps = [
  {
    id: 1,
    title: "Welcome to the Sterile Mate Portal",
    subtitle: "Your Healthcare Solutions Partner",
    description: "We're here to help you with water purification, sterile processing requests, and backordered items. Let's get you started!",
    icon: null, // Will use logo instead
    useLogo: true,
    color: "from-blue-500 to-cyan-500",
    bgColor: "from-blue-50 to-cyan-50"
  },
  {
    id: 2,
    title: "Report Maintenance Issues",
    subtitle: "Quick & Easy Service Requests",
    description: "Need help with leaks, plumbing, or equipment repairs? Our maintenance portal lets you submit requests in seconds and track their status.",
    icon: Wrench,
    color: "from-emerald-500 to-teal-500",
    bgColor: "from-emerald-50 to-teal-50",
    features: ["Report water leaks & plumbing issues", "Request ATP device repairs", "Emergency service requests", "Track repair status"]
  },
  {
    id: 3,
    title: "Order Supplies & Equipment",
    subtitle: "Backorders",
    description: "Browse our catalog, request quotes instantly, or submit backorder requests for hard-to-find items.",
    icon: ShoppingCart,
    color: "from-violet-500 to-purple-500",
    bgColor: "from-violet-50 to-purple-50",
    features: ["CleanRead ATP devices", "ATP, Protein & Aqua swabs", "Instrusponges (all sizes)", "Visual inspection borescopes"]
  },
  {
    id: 4,
    title: "Save Your Profile",
    subtitle: "Auto-Fill on Every Visit",
    description: "Save your hospital information once, and we'll remember you! Your details will auto-fill on future visits for faster submissions.",
    icon: Building2,
    color: "from-amber-500 to-orange-500",
    bgColor: "from-amber-50 to-orange-50",
    features: ["One-time profile setup", "Auto-fill on all forms", "Stored securely on your device", "Update anytime"]
  },
  {
    id: 5,
    title: "You're All Set!",
    subtitle: "Start Using Sterile Mate",
    description: "You're ready to go! Use the navigation below to submit maintenance requests or order supplies.",
    icon: CheckCircle2,
    color: "from-green-500 to-emerald-500",
    bgColor: "from-green-50 to-emerald-50"
  }
];

// Staff Onboarding Steps
const staffSteps = [
  {
    id: 1,
    title: "Welcome to the Sterile Mate Portal",
    subtitle: "Manage Requests & Operations",
    description: "As a Sterile Mate team member, you have access to powerful tools for managing maintenance requests and customer orders.",
    icon: null,
    useLogo: true,
    color: "from-indigo-500 to-blue-500",
    bgColor: "from-indigo-50 to-blue-50"
  },
  {
    id: 2,
    title: "Dashboard Overview",
    subtitle: "Your Command Center",
    description: "Get a bird's eye view of all activity - pending requests, recent orders, team stats, and more.",
    icon: ClipboardList,
    color: "from-blue-500 to-cyan-500",
    bgColor: "from-blue-50 to-cyan-50",
    features: ["Real-time request tracking", "Order management", "Team performance stats", "Quick action buttons"]
  },
  {
    id: 3,
    title: "User Management",
    subtitle: "For Administrators",
    description: "Add new team members, assign roles, and manage permissions. Control who can access what.",
    icon: Users,
    color: "from-violet-500 to-purple-500",
    bgColor: "from-violet-50 to-purple-50",
    features: ["Add Hub Admins", "Add Maintenance Workers", "Set access permissions", "Manage user accounts"]
  },
  {
    id: 4,
    title: "Work Order Management",
    subtitle: "For Maintenance Workers",
    description: "View assigned requests, update work status, log scope of work, and request additional parts.",
    icon: FileText,
    color: "from-emerald-500 to-teal-500",
    bgColor: "from-emerald-50 to-teal-50",
    features: ["View assigned work", "Update job status", "Log work performed", "Request parts"]
  },
  {
    id: 5,
    title: "Stay Notified",
    subtitle: "Never Miss an Update",
    description: "Get instant notifications for new assignments, status changes, and important updates. Check the bell icon anytime!",
    icon: Bell,
    color: "from-amber-500 to-orange-500",
    bgColor: "from-amber-50 to-orange-50",
    features: ["In-app notifications", "Email alerts", "Assignment notices", "Status updates"]
  },
  {
    id: 6,
    title: "Ready to Work!",
    subtitle: "Let's Get Started",
    description: "You're all set! Head to your dashboard to view pending tasks and start managing requests.",
    icon: CheckCircle2,
    color: "from-green-500 to-emerald-500",
    bgColor: "from-green-50 to-emerald-50"
  }
];

export default function OnboardingModal({ isStaff = false, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  
  const steps = isStaff ? staffSteps : hospitalSteps;
  const storageKey = isStaff ? "sterilemate_staff_onboarded" : "sterilemate_hospital_onboarded";
  
  useEffect(() => {
    // Check if user has already seen onboarding
    const hasSeenOnboarding = localStorage.getItem(storageKey);
    if (hasSeenOnboarding === "true") {
      setIsVisible(false);
      onComplete?.();
    }
  }, [storageKey, onComplete]);
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSkip = () => {
    handleComplete();
  };
  
  const handleComplete = () => {
    localStorage.setItem(storageKey, "true");
    setIsVisible(false);
    onComplete?.();
  };
  
  if (!isVisible) return null;
  
  const step = steps[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === steps.length - 1;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
        onClick={handleSkip}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Skip Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 z-10 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
          data-testid="onboarding-skip"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Header with gradient */}
        <div className={`relative bg-gradient-to-br ${step.bgColor} px-8 pt-10 pb-8`}>
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/30 rounded-full blur-xl" />
          
          {/* Icon or Logo */}
          {step.useLogo ? (
            <div className="relative w-24 h-24 mx-auto mb-6 bg-white rounded-2xl flex items-center justify-center shadow-lg p-3">
              <img 
                src={STERILE_MATE_LOGO} 
                alt="Sterile Mate" 
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className={`relative w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center shadow-lg animate-bounce-subtle`}>
              {Icon && <Icon className="w-10 h-10 text-white" />}
            </div>
          )}
          
          {/* Title */}
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {step.title}
          </h2>
          <p className={`text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r ${step.color} text-center`}>
            {step.subtitle}
          </p>
        </div>
        
        {/* Content */}
        <div className="px-8 py-6">
          <p className="text-slate-600 text-center mb-6 leading-relaxed">
            {step.description}
          </p>
          
          {/* Features list */}
          {step.features && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              {step.features.map((feature, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl animate-fade-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0`}>
                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm text-slate-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? `bg-gradient-to-r ${step.color} w-8` 
                    : idx < currentStep 
                      ? 'bg-slate-400' 
                      : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex-1 h-12 rounded-xl font-semibold transition-all ${
                currentStep === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50'
              }`}
              data-testid="onboarding-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            
            <Button
              onClick={handleNext}
              className={`flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r ${step.color} hover:opacity-90 text-white shadow-lg transition-all`}
              data-testid="onboarding-next"
            >
              {isLastStep ? (
                <>
                  Get Started
                  <Sparkles className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
          
          {/* Skip text */}
          <button
            onClick={handleSkip}
            className="w-full mt-4 text-sm text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="onboarding-skip-text"
          >
            Skip tutorial
          </button>
        </div>
      </div>
    </div>
  );
}
